/**
 * `/forgot-password` — ask for a reset link.
 *
 * The confirmation is shown for every address, whether or not it has an
 * account. The worker deliberately refuses to say which (answering otherwise
 * would let a stranger test a list of addresses for customers), and a UI that
 * said "no such account" would hand that straight back.
 */

import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useSession } from "../../auth/AuthProvider";
import { AuthError } from "../../auth/authApi";
import { AuthCard, Field, firstMissing } from "./AuthCard";

export function ForgotPassword() {
  const { api } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;

    const missing = firstMissing([{ label: "Email address", value: email }]);
    if (missing) {
      setError(missing);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await api.requestPasswordReset(email);
      navigate("/check-email", { replace: true, state: { email, reason: "reset" } });
    } catch (cause) {
      // Only a failure to *ask* — a network error or a rate limit. A refusal to
      // say whether the account exists never reaches here.
      setError(cause instanceof AuthError ? cause.message : "Could not reach the server. Please try again.");
      setBusy(false);
    }
  }

  return (
    <AuthCard
      title="Reset your password"
      intro="Give us the address on the account and we'll email you a link to choose a new password."
      error={error}
      footer={
        <>
          Remembered it? <Link to="/login">Sign in</Link>.
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        <Field
          id="email"
          label="Email address"
          type="email"
          value={email}
          autoComplete="email"
          onChange={setEmail}
        />
        <button type="submit" className="button button--primary auth__submit" disabled={busy}>
          {busy ? "Sending…" : "Send the link"}
        </button>
      </form>
    </AuthCard>
  );
}
